import { ValueStatus } from '../types/protocols';
import { BaseType } from '../types/Enums';
import type { BaseComponent, ValueWithMetadata } from '../types/protocols';
import type { DeletedFileTombstone } from '../api/fileDeletionApi';

/**
 * Deleting a building component (workspace CLAUDE.md, Buildings), in one root update, the
 * same rule as iOS `ComponentDeletion`:
 * - its locks (`parentKey`) are deleted with it: a lock means nothing without its opening;
 * - every other component that points at it (`parentSurface`, `parentSurface1/2`,
 *   `parentComponentKey`) is kept and only that pointer is cleared. An opening that loses
 *   one slot keeps the other in `parentSurface1`;
 * - the pins other components have in its photos are removed;
 * - the photos and documents of every deleted node are moved to `deletedFiles` tombstones.
 *
 * The checks about a deleted node are not part of the update: the server trigger on the
 * component node removes them (completions kept). `componentDeletionEffects` only counts
 * them, for the confirmation text.
 */

type ComponentMap = { [key: string]: BaseComponent };

/** The building's checks, `inspections/active/{b}`, keyed by check key; only `componentKey` is read. */
export type BuildingCheckMap = { [checkKey: string]: { componentKey?: string } | null | undefined };

export interface ComponentDeletionEffects {
    /** The component's locks, deleted with it. */
    lockKeys: string[];
    /** Components that point at it and are kept, with the pointer cleared. */
    detachedKeys: string[];
    /** Checks about the component or one of its locks; the server trigger removes them. */
    checkKeys: string[];
}

const pointsAt = (component: BaseComponent, componentKey: string): boolean =>
    component.parentComponentKey === componentKey ||
    component.parentSurface?.value === componentKey ||
    component.parentSurface1?.value === componentKey ||
    component.parentSurface2?.value === componentKey;

/** What deleting `componentKey` takes with it and what it detaches, archived components included. */
export const componentDeletionEffects = (
    components: ComponentMap | undefined,
    componentKey: string,
    checks: BuildingCheckMap = {}
): ComponentDeletionEffects => {
    const lockKeys: string[] = [];
    const detachedKeys: string[] = [];
    Object.entries(components || {}).forEach(([key, component]) => {
        if (key === componentKey || !component) return;
        if (component.type === BaseType.LockMechanism) {
            if (component.parentKey?.value === componentKey) lockKeys.push(key);
        } else if (pointsAt(component, componentKey)) {
            detachedKeys.push(key);
        }
    });
    const deletedKeys = new Set([componentKey, ...lockKeys]);
    const checkKeys = Object.entries(checks || {})
        .filter(([, check]) => check?.componentKey !== undefined && deletedKeys.has(check.componentKey))
        .map(([checkKey]) => checkKey);
    return { lockKeys: lockKeys.sort(), detachedKeys: detachedKeys.sort(), checkKeys: checkKeys.sort() };
};

/** Root-relative multi-path update that deletes the component as described above. */
export const componentDeletionUpdates = (
    components: ComponentMap | undefined,
    buildingKey: string,
    componentKey: string,
    deletedBy: string,
    deletedAt: string
): Record<string, unknown> => {
    const base = `buildings/${buildingKey}/structure/components`;
    const { lockKeys, detachedKeys } = componentDeletionEffects(components, componentKey);
    const deletedKeys = [componentKey, ...lockKeys];
    const updates: Record<string, unknown> = {};

    const cleared: ValueWithMetadata<string> = {
        value: null,
        updatedBy: deletedBy,
        updatedWhen: deletedAt,
        status: ValueStatus.notSet,
    };

    deletedKeys.forEach(key => {
        updates[`${base}/${key}`] = null;
        const component = components?.[key];
        Object.entries(component?.photos || {}).forEach(([photoKey, photo]) => {
            const tombstone: DeletedFileTombstone = {
                originalPath: `${base}/${key}/photos/${photoKey}`,
                entityType: 'componentPhoto',
                storagePath: photo.storagePath || '',
                metadata: photo,
                deletedBy,
                deletedAt,
            };
            updates[`deletedFiles/${photoKey}`] = tombstone;
        });
        Object.entries(component?.documents || {}).forEach(([documentKey, document]) => {
            const tombstone: DeletedFileTombstone = {
                originalPath: `${base}/${key}/documents/${documentKey}`,
                entityType: 'componentDocument',
                storagePath: `${base}/${key}/documents/${document.storageFilename}`,
                metadata: document,
                deletedBy,
                deletedAt,
            };
            updates[`deletedFiles/${documentKey}`] = tombstone;
        });
    });

    detachedKeys.forEach(key => {
        const component = components![key];
        const path = `${base}/${key}`;
        if (component.parentSurface?.value === componentKey) {
            updates[`${path}/parentSurface`] = cleared;
        }
        const inSlot1 = component.parentSurface1?.value === componentKey;
        const inSlot2 = component.parentSurface2?.value === componentKey;
        const kept = component.parentSurface2?.value;
        if (inSlot1 && !inSlot2 && kept) {
            // The remaining parent moves to slot 1: the editors and readers take slot 1 first.
            updates[`${path}/parentSurface1`] = { ...cleared, value: kept, status: ValueStatus.set };
            updates[`${path}/parentSurface2`] = cleared;
        } else {
            if (inSlot1) updates[`${path}/parentSurface1`] = cleared;
            if (inSlot2) updates[`${path}/parentSurface2`] = cleared;
        }
        if (component.parentComponentKey === componentKey) {
            updates[`${path}/parentComponentKey`] = null;
        }
    });

    Object.entries(components || {}).forEach(([key, component]) => {
        if (deletedKeys.includes(key)) return;
        Object.entries(component?.photoLocations || {}).forEach(([photoKey, location]) => {
            if (location?.parentKey === componentKey) {
                updates[`${base}/${key}/photoLocations/${photoKey}`] = null;
            }
        });
    });

    return updates;
};
